import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import css from "components/mobile/meeting/calendar/calendar.module.css";
import dayjs from "dayjs";
import Header from "components/mobile/meeting/calendar/Header";
import Body from "components/mobile/meeting/calendar/Body";
import { ConfigProvider, FloatButton, Spin } from "antd";
import {
  CalendarOutlined,
  PlusOutlined,
  SwapRightOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ResPop from "components/mobile/mypage/ResPop";
import { useSelector } from "react-redux";
import DetailResPop from "components/mobile/mypage/DetailResPop";
import AllSegs from "components/mobile/meeting/calendar/AllSegs";

const locale = "ko";
const monthView = "dayGridMonth";
const weekView = "timeGridWeek";
const currentType = "current";
const pastType = "past";
const initialView = monthView;
const viewList = [monthView, weekView];

/**
 * 파일명: calendar.js
 * 작성자: 권도훈
 * 작성일자: 25.02.24
 * 설명: 모바일 페이지 캘린더
 */
function Calendar() {
  // redux
  const payload = useSelector((state) => state.user);
  const systemPayload = useSelector((state) => state.system);

  const [init, setInit] = useState(true);
  const [loading, setLoading] = useState(true);

  // calendar state
  const [date, setDate] = useState(dayjs(new Date()));
  const [schedules, setSchedules] = useState([]);
  const [calendar, setCalendar] = useState();
  const [view, setView] = useState(initialView);

  // reserve popup state
  const [openRes, setOpenRes] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [openMore, setOpenMore] = useState(false);
  const [reserveType, setReserveType] = useState("");
  const [detailType, setDetailType] = useState("");
  const [reserveInfo, setReserveInfo] = useState({
    EMAIL: payload.id,
    HOST_NM: payload.name,
    INPUT_ID: payload.id,
    CONTENTS: "",
    DURATION: 0,
    END_DATE: "",
    END_TIME: "",
    FILE_INFO: "",
    MAIL_YN: "N",
    MEETING_PW: "",
    OPEN_YN: "Y",
    RECORD_YN: "N",
    SCHEDULE_ID: "",
    SCHEDULE_TYPE: "",
    START_DATE: "",
    START_TIME: "",
    TITLE: "",
    ENDPOINT_LIST: [],
    PARTICIPANT_LIST: [],
    ATTACHMENT_LIST: [],
    USE_YN: "Y",
    PROCESS_TYPE: "",
  });
  const [allSegs, setAllSegs] = useState([]);

  const getWeekRange = useCallback((date) => {
    const currentDay = new Date(date).getDay(); // 요일 (0 = 일요일, 1 = 월요일, ..., 6 = 토요일)
    // 지난주 일요일 찾기 (현재 날짜에서 현재 요일만큼 빼기)
    const lastSunday = new Date(date);
    lastSunday.setDate(new Date(date).getDate() - currentDay); // 지난주 일요일

    // 이번 주 토요일 찾기 (현재 날짜에서 남은 요일만큼 더하기)
    const saturday = new Date(date);
    saturday.setDate(new Date(date).getDate() + (6 - currentDay)); // 이번 주 토요일

    return {
      start: lastSunday.toISOString().split("T")[0], // YYYY-MM-DD 형식
      end: saturday.toISOString().split("T")[0],
    };
  }, []);

  const getLastDate = useCallback((date) => {
    return new Date(date.format("YYYY"), date.format("MM"), 0).getDate();
  }, []);

  const getSchedules = useCallback(() => {
    setLoading(true);

    let startDate = "";
    let endDate = "";

    switch (view) {
      case monthView:
        startDate = date.format("YYYY-MM-01");
        endDate = date.format("YYYY-MM-") + getLastDate(date);
        break;
      case weekView:
        const weekRange = getWeekRange(date);
        startDate = weekRange.start;
        endDate = weekRange.end;
        break;
      default:
        return;
    }

    const config = {
      params: {
        ACTIVE_START: startDate + " 00:00:00",
        ACTIVE_END: endDate + " 23:59:59",
        CALENDAR_TYPE: "MY",
      },
    };

    axios
      .get("/schedule/list", config)
      .then((res) => {
        if (res.status === 200) {
          const scheduleList = res.data.SCHEDULE;

          if (scheduleList !== undefined) {
            setSchedules(
              scheduleList.map((schedule) => {
                return {
                  id: schedule.SCHEDULE_ID,
                  title: schedule.TITLE,
                  start: schedule.START_TIME,
                  end: schedule.END_TIME,
                  data: schedule,
                };
              })
            );
          }
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [date, getLastDate, getWeekRange, view]);

  const resetReserveInfo = useCallback(() => {
    setReserveInfo({
      ...reserveInfo,
      EMAIL: "",
      HOST_NM: "",
      INPUT_ID: "",
      CONTENTS: "",
      DURATION: 0,
      END_DATE: "",
      END_TIME: "",
      FILE_INFO: "",
      MAIL_YN: "N",
      MEETING_PW: "",
      OPEN_YN: "Y",
      RECORD_YN: "N",
      SCHEDULE_ID: "",
      SCHEDULE_TYPE: "",
      START_DATE: "",
      START_TIME: "",
      TITLE: "",
      ENDPOINT_LIST: [],
      PARTICIPANT_LIST: [],
      ATTACHMENT_LIST: [],
      USE_YN: "Y",
      PROCESS_TYPE: "",
    });
  }, [reserveInfo]);

  useLayoutEffect(() => {
    if (init) {
      getSchedules();
      setInit(false);
    }
  }, [getSchedules, init]);

  useEffect(() => {
    if (reserveInfo.PROCESS_TYPE !== "" && !openRes && !openDetail) {
      resetReserveInfo();
    }
  }, [openDetail, openRes, reserveInfo.PROCESS_TYPE, resetReserveInfo]);

  const onRefresh = () => {
    setInit(true);
  };

  const onChangeView = () => {
    let type = "";
    switch (calendar.view.type) {
      case weekView:
        type = monthView;
        break;
      case monthView:
        type = weekView;
        break;
      default:
        return;
    }
    calendar.changeView(type);
    setView(type);
  };

  const onPrev = () => {
    calendar.prev();
    setDate(dayjs(calendar.currentData.currentDate));
    setInit(true);
  };

  const onNext = () => {
    calendar.next();
    setDate(dayjs(calendar.currentData.currentDate));
    setInit(true);
  };

  const onChangeDate = (date) => {
    setDate(dayjs(date));
    calendar.gotoDate(new Date(dayjs(date).format()));
    setInit(true);
  };

  const onOpenReserve = (reserveType) => {
    setReserveInfo({
      ...reserveInfo,
      SCHEDULE_TYPE: systemPayload.systemType,
      PROCESS_TYPE: "C",
    });
    setReserveType(reserveType);
    setOpenRes(true);
  };

  const onOpenEdit = (data) => {
    if (
      new Date() > new Date(data.START_TIME) ||
      new Date() > new Date(data.END_TIME)
    ) {
      setDetailType(pastType);
    } else {
      setDetailType(currentType);
    }

    const schedule = {
      ...reserveInfo,
      EMAIL: data.HOST_EMAIL,
      HOST_NM: data.HOST_NM,
      INPUT_ID: data.INPUT_ID,
      CONTENTS: data.CONTENTS,
      DURATION: data.DURATION,
      END_DATE: data.END_DATE,
      END_TIME: data.END_TIME,
      FILE_INFO: data.FILE_INFO,
      MAIL_YN: data.MAIL_YN,
      MEETING_PW: data.MEETING_PW,
      OPEN_YN: data.OPEN_YN,
      RECORD_YN: data.RECORD_YN,
      SCHEDULE_ID: data.SCHEDULE_ID,
      SCHEDULE_TYPE: data.SCHEDULE_TYPE,
      START_DATE: data.START_DATE,
      START_TIME: data.START_TIME,
      TITLE: data.TITLE,
      ENDPOINT_LIST: data.ENDPOINT_LIST,
      PARTICIPANT_LIST: data.PARTICIPANT_LIST,
      USE_YN: data.USE_YN,
      PROCESS_TYPE: "U",
    };
    setReserveInfo(schedule);
    setOpenDetail(true);
  };

  const onCloseResPop = () => {
    setReserveType("");
    setOpenRes(false);
  };

  const onCloseDetail = () => {
    setOpenDetail(false);
  };

  const onOpenMore = (list) => {
    setAllSegs(list);
    setOpenMore(true);
  };

  const onCloseMore = () => {
    setAllSegs([]);
    setOpenMore(false);
  };

  const onEditSchedule = (res, process_type) => {
    setReserveInfo({
      ...reserveInfo,
      ENDPOINT_LIST: res.endpointlist,
      PARTICIPANT_LIST: res.participantlist,
      ATTACHMENT_LIST: res.attachmentlist,
      PROCESS_TYPE: process_type,
    });
    setReserveType("general");
    setOpenRes(true);
  };

  return (
    <section className={css.wrapper}>
      <section className={css.container}>
        {loading && (
          <section className={css.loading}>
            <Spin />
          </section>
        )}

        <ResPop
          isOpenRes={openRes && reserveType !== ""}
          close={onCloseResPop}
          schedule={reserveInfo}
          getScheduleList={onRefresh}
          PAGE_TYPE="calendar"
          RES_TYPE={reserveType}
        />

        <DetailResPop
          isOpenResDetail={openDetail}
          openResPop={onEditSchedule}
          closeResDetail={onCloseDetail}
          schedule={reserveInfo}
          type={detailType}
          refresh={onRefresh}
        />

        <AllSegs
          open={openMore}
          onClose={onCloseMore}
          list={allSegs}
          onEdit={(res) => onOpenEdit(res)}
        />

        <Header
          date={date}
          view={view}
          viewList={viewList}
          monthView={monthView}
          onChangeView={onChangeView}
          onPrev={onPrev}
          onNext={onNext}
          onChangeDate={onChangeDate}
          onReserveNow={() => onOpenReserve("easy")}
        />

        <Body
          schedules={schedules}
          view={view}
          locale={locale}
          calendarApi={calendar}
          setCalendarApi={(res) => setCalendar(res)}
          onChangeDate={onChangeDate}
          onChangeView={onChangeView}
          onEdit={(res) => onOpenEdit(res)}
          onOpenMore={(res) => onOpenMore(res)}
        />

        <ReserveButton
          onReserveNow={() => onOpenReserve("easy")}
          onReserve={() => onOpenReserve("general")}
        />
      </section>
    </section>
  );
}

export default Calendar;

function ReserveButton({ onReserveNow, onReserve }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {open && <section className={css.float_background} />}
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: "#1cb691",
            colorPrimaryHover: "#1cb691",
            controlHeightLG: 52,
          },
        }}
      >
        <FloatButton.Group
          trigger="click"
          type="primary"
          icon={<PlusOutlined />}
          open={open}
          onOpenChange={(open) => setOpen(open)}
        >
          <div className={css.float_item}>
            <p className={css.float_title}>즉시 미팅</p>
            <FloatButton
              icon={<SwapRightOutlined className={css.float_icon} />}
              onClick={() => {
                onReserveNow();
                setOpen(false);
              }}
            />
          </div>

          <div className={css.float_item}>
            <p className={css.float_title}>일정 예약</p>
            <FloatButton
              icon={<CalendarOutlined className={css.float_icon} />}
              onClick={() => {
                onReserve();
                setOpen(false);
              }}
            />
          </div>
        </FloatButton.Group>
      </ConfigProvider>
    </>
  );
}
